import React, { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { MultiValue, MultiValueGenericProps } from "react-select"

import FormFieldWrapper from "components/Form/FormFieldWrapper"
import Icon from "components/Icons"
import MultipleSelect, {
  MultipleSelectOption
} from "components/Inputs/Select/MultipleSelect"
import { Tag } from "components/Tag/Tag"
import { TooltipComponent } from "components/ui/tooltip"
import { AppState } from "store/AppState"
import { selectPKDList } from "store/pkd"
import { PKD } from "types/Template"
import usePrefix from "utils/usePrefix"

interface PkdSelectProps {
  pkds: PKD[]
  logicalAlternativePkds: PKD[]
  formType: "client" | "news"
  isDisabled: boolean
  setValues: (pkds: PKD[], logicalAlternativePkds: PKD[]) => void
  showErrorStyles?: boolean
}

export const PkdSelect = ({
  pkds,
  logicalAlternativePkds,
  formType,
  isDisabled,
  setValues,
  showErrorStyles
}: PkdSelectProps) => {
  const t = usePrefix("Tags")
  const allPKDs = useSelector((s: AppState) => selectPKDList(s, formType))

  const value = useMemo(() => {
    return allPKDs.filter(({ value: pkdValue, version: pkdVersion }) =>
      [...pkds, ...logicalAlternativePkds].some(
        (item) => item.value === pkdValue && item.version === pkdVersion
      )
    )
  }, [allPKDs, logicalAlternativePkds, pkds])

  const options = useMemo(() => {
    return allPKDs.map(({ value, label, level, version }) => ({
      value,
      label: level === 0 ? value : `${value} (${version}) - ${label}`,
      level,
      version,
      optionDisabled: level === 0
    }))
  }, [allPKDs])

  const handleSelectChange = useCallback(
    (newValue: MultiValue<MultipleSelectOption>) => {
      const selectedPkds: PKD[] = []
      const selectedAlternativeTags: PKD[] = []

      newValue.forEach(({ value, version }) => {
        if (!value || typeof value !== "string" || !version) return

        if (
          pkds.some((pkd) => pkd.value === value && pkd.version === version)
        ) {
          selectedPkds.push({ value, version })
        } else if (
          logicalAlternativePkds.some(
            (pkd) => pkd.value === value && pkd.version === version
          )
        ) {
          selectedAlternativeTags.push({ value, version })
        } else {
          selectedPkds.push({ value, version })
        }
      })

      setValues(selectedPkds, selectedAlternativeTags)
    },
    [logicalAlternativePkds, pkds, setValues]
  )

  const handleChangeOptional = useCallback(
    (pkd: PKD) => (isChecked: boolean, isIndeterminate: boolean) => {
      if (isIndeterminate) {
        setValues(
          pkds.filter(
            ({ value, version }) =>
              !(value === pkd.value && version === pkd.version)
          ),
          [...logicalAlternativePkds, pkd]
        )
      } else if (formType === "news") {
        setValues(
          [...pkds, pkd],
          logicalAlternativePkds.filter(
            ({ value, version }) =>
              !(value === pkd.value && version === pkd.version)
          )
        )
      }
    },
    [formType, logicalAlternativePkds, pkds, setValues]
  )

  const MultiValueContainer = (
    props: MultiValueGenericProps<MultipleSelectOption>
  ) => {
    const { value, label: initialLabel, version } = props.data
    const isChecked = pkds.some(
      (pkd) => pkd.value === value && pkd.version === version
    )
    const isIndeterminate =
      formType === "news" &&
      logicalAlternativePkds.some(
        (pkd) => pkd.value === value && pkd.version === version
      )

    const label = `${value} (${version}) - ${initialLabel}`

    return (
      <div className="m-[2px]">
        <Tag
          checked={isChecked}
          isIndeterminate={isIndeterminate}
          formType={formType}
          tooltip={typeof initialLabel === "string" ? label : ""}
          onChange={handleChangeOptional({ value, version })}
          onClose={() => {
            if (isIndeterminate) {
              setValues(
                pkds,
                logicalAlternativePkds.filter(
                  (pkd) => !(pkd.value === value && pkd.version === version)
                )
              )
            } else {
              setValues(
                pkds.filter(
                  (pkd) => !(pkd.value === value && pkd.version === version)
                ),
                logicalAlternativePkds
              )
            }
          }}
        >
          {`${value} (${version})`}
        </Tag>
      </div>
    )
  }

  return (
    <FormFieldWrapper
      name="pkds"
      label={
        <span className="inline-flex items-center gap-1.5">
          {t("pkd_label")}
          <span className="text-muted-foreground">
            {t("pkd_label_additional_info")}
          </span>
          <TooltipComponent
            tooltipContent={t("pkd_label_tooltip")}
            triggerContent={
              <span>
                <Icon
                  type="infoRound"
                  className="h-4 w-4 stroke-secondary-foreground"
                />
              </span>
            }
          />
        </span>
      }
    >
      <MultipleSelect
        name="pkds"
        virtualList
        placeholder={t("select_placeholder")}
        hideSelectedOptions={false}
        additionalPadding={formType === "news"}
        isClearable
        components={{
          MultiValueContainer
        }}
        value={value}
        getOptionValue={({ value, version }) => `${value}-${version}`}
        onSelectChange={handleSelectChange}
        options={options}
        isDisabled={isDisabled}
        showErrorStyles={showErrorStyles}
      />
    </FormFieldWrapper>
  )
}
